import React from "react"
import { Box, Typography, useTheme } from "@mui/material"
import { CallIcon, MailIcon } from "@/components/icons"

export type RestaurantContactActionsProps = {
  phone: string | null | undefined
  email: string | null | undefined
}

function trimmed(value: string | null | undefined): string | null {
  const next = value?.trim()
  return next ? next : null
}

function ContactRow({
  href,
  label,
  value,
  icon,
}: {
  href: string
  label: string
  value: string
  icon: React.ReactNode
}) {
  const theme = useTheme()
  return (
    <Box
      component="a"
      href={href}
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: theme.spacing(1.25),
        minHeight: 44,
        py: theme.spacing(0.75),
        px: theme.spacing(0.5),
        borderRadius: theme.spacing(1),
        color: "inherit",
        textDecoration: "none",
        overflowWrap: "anywhere",
        "&:hover": { bgcolor: theme.palette.action.hover },
        "&:focus-visible": {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: 2,
        },
      }}
    >
      <Box
        aria-hidden
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          width: 36,
          height: 36,
          borderRadius: theme.spacing(1),
          bgcolor: theme.palette.action.hover,
          color: theme.palette.text.secondary,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column", gap: theme.spacing(0.25) }}>
        <Typography variant="body2" component="span" sx={{ fontWeight: theme.typography.fontWeightBold, lineHeight: 1.3 }}>
          {label}
        </Typography>
        <Typography
          variant="caption"
          component="span"
          color="text.secondary"
          sx={{ lineHeight: 1.35, overflowWrap: "anywhere" }}
        >
          {value}
        </Typography>
      </Box>
    </Box>
  )
}

/** Call / email actions from the current storefront restaurant. Renders nothing when both are missing. */
export function RestaurantContactActions({ phone, email }: RestaurantContactActionsProps) {
  const tel = trimmed(phone)
  const mail = trimmed(email)
  if (!tel && !mail) return null

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, alignItems: "stretch" }}>
      {tel ? (
        <ContactRow
          href={`tel:${tel}`}
          label="Call restaurant"
          value={tel}
          icon={<CallIcon width={18} height={18} />}
        />
      ) : null}
      {mail ? (
        <ContactRow
          href={`mailto:${mail}`}
          label="Email restaurant"
          value={mail}
          icon={<MailIcon width={18} height={18} />}
        />
      ) : null}
    </Box>
  )
}
